import { type IPdfBlock } from '@mahawi/eshop-common/dist/src/types';
import React from 'react';
import { Link } from 'react-router-dom';

import {
  type ITermsContent,
  type ITermsSection,
} from '../../../legal/terms-content';
import LegalBlock from './legal-block';

export interface ITermsDocumentLabels {
  downloadOrderDocuments: string;
  downloadWithdrawalForm: string;
  withdrawalLink: string;
}

export default function TermsDocument({
  content,
  language,
  versionCode,
  labels,
}: {
  content: ITermsContent;
  language: string;
  versionCode: string;
  labels: ITermsDocumentLabels;
}): React.JSX.Element {
  const { withdrawalForm } = content;

  return (
    <>
      <h1>{content.title}</h1>
      <p className="terms--subtitle">{content.subtitle}</p>
      <p className="terms--version">{content.versionLabel}</p>

      <ul className="terms--downloads">
        <li>
          <a
            href={`/${language}/legal/order-documents/${versionCode}.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {labels.downloadOrderDocuments}
          </a>
        </li>
        <li>
          <a
            href={`/${language}/legal/withdrawal-form.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {labels.downloadWithdrawalForm}
          </a>
        </li>
      </ul>

      {content.sections.map((section: ITermsSection): React.JSX.Element => (
        <section key={section.id} id={section.id}>
          <h2>{section.heading}</h2>
          {section.blocks.map(
            (block: IPdfBlock, index: number): React.JSX.Element => (
              <LegalBlock key={`block-${index}`} block={block} />
            ),
          )}
          {section.id === 'withdrawal' ? (
            <p>
              <Link
                to={`/${language}/withdrawal`}
                className="terms--withdrawal-link"
              >
                {labels.withdrawalLink}
              </Link>
            </p>
          ) : null}
        </section>
      ))}

      <section id="withdrawalForm" className="terms--withdrawal-form">
        <h2>{withdrawalForm.heading}</h2>
        <p>{withdrawalForm.intro}</p>
        <p>{withdrawalForm.addressee}</p>
        <p>{withdrawalForm.statement}</p>
        {withdrawalForm.fields.map(
          (field: string, index: number): React.JSX.Element => (
            <div key={`field-${index}`} className="terms--form-field">
              <span>{field}</span>
              <span className="terms--form-line" aria-hidden="true" />
            </div>
          ),
        )}
        <p className="terms--footnote">{withdrawalForm.footnote}</p>
        <p>
          <a
            href={`/${language}/legal/withdrawal-form.pdf`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {labels.downloadWithdrawalForm}
          </a>
        </p>
      </section>
    </>
  );
}
